import { memo, useEffect, useMemo } from "react";
import { Position } from "../../proto/generated/position";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Stack, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { getGroupedPositions } from "../../util/positions";
import { useStyles } from "./styles";
import PositionsTableRow from "./positionsTableRow";
import { useDispatch } from "react-redux";
import { fetchSecuritySnapshotsBulk, fetchGreeksBulk } from "../../services/apis/candles";
import { getOrderBooksBulk } from "../../util/tradepanel";
import { updateOrderBooks } from "../../reducer/security.reducer";
import websocketActions from "../../actions/websocket.actions";
import { GroupedPosition } from "../../reducer/positions.reducer";
import { selectGroupBy } from "../../selectors/positions";
import { Tooltip } from "@mui/material";
import Info from "../../assets/icons/InfoIcon";
import { WebSocketRequestMessage_Subscription } from "../../proto/generated/websocket";

export type PositionsTableProps = {
    positions: Position[],
    showActions?: boolean,
    showGreeks?: boolean
}

const PositionsTable: React.FC<PositionsTableProps> = ({positions, showActions = true, showGreeks = false}) => {

    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const groupBy = useSelector(selectGroupBy);

    const groupedPositions: GroupedPosition[] = useMemo(() => {

        const grouped: Record<string, GroupedPosition> = getGroupedPositions(positions, groupBy)

        return Object.values(grouped)
    }, [positions, groupBy])

    const securities = useMemo(() => {
            return Object.values(positions || {}).map(pos => pos.security) || [];
        }, [positions]);

    useEffect(() => {

        if (!securities.length) return;

        fetchSecuritySnapshotsBulk({ securities, dispatch });
        getOrderBooksBulk(securities).then((orderBooks) => {
            dispatch(updateOrderBooks(orderBooks));
        });

        if (showGreeks) {
            fetchGreeksBulk({ securities, dispatch });
        }

        const subscribeList: Partial<WebSocketRequestMessage_Subscription> = {
            orderBookSubscriptions: [
                {
                    tag: 'positions-table-order-books',
                    securities,
                    topBidAskOnly: true,
                }
            ],
            securitySnapshotSubscriptions: [
                {
                    tag: 'positions-table-snapshots',
                    securities
                }
            ]
        }

        if (showGreeks) {
            subscribeList.candleSubscriptions = [
                {
                    tag: 'position-greek-subscription',
                    securities,
                    timeBucket: { timeSeries: 1, value: 1 }
                }
            ]
        }
        
        websocketActions.setWebsocketSubscribeList(dispatch, subscribeList)
    }, [securities, showGreeks])

    // const expandablePositions = useMemo(() => !!groupedPositions.find(p => p.children?.length > 1), [groupedPositions])
    const expandablePositions = useMemo(() => !!groupedPositions.find(p => !p.groupId.startsWith("default_")), [groupedPositions])

    const tableHeader = useMemo(() => <TableHead>
        <TableRow className={classes.headerRow}>
            <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                <Typography variant="caption" color="textSecondary">SYMBOL</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                <Typography variant="caption" color="textSecondary">{showGreeks ? 'DELTA' : 'QTY'}</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.averageCell)}>
                <Typography variant="caption" color="textSecondary">{showGreeks ? 'THETA' : 'AVG'}</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                <Typography variant="caption" color="textSecondary">{showGreeks ? 'GAMMA' : 'LTP'}</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.profitCell)}>
                <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end" sx={{ height: '100%' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1 }}>
                        {showGreeks ? 'VEGA' : 'P/L'}
                    </Typography>
                    {!showGreeks && (
                        <Tooltip 
                            title="Unrealized P&L for open positions, Realized P&L for closed positions" 
                            placement="top"
                            arrow
                        >
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                <Info sx={{ fontSize: '12px', color: 'text.secondary', cursor: 'pointer' }} />
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    </TableHead>, [expandablePositions, showGreeks])


    return <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <Table className={classes.table} stickyHeader>
            {tableHeader}
            <TableBody>
                {groupedPositions.map((position) => <PositionsTableRow position={position} expandEnabled={expandablePositions} showGreeks={showGreeks} />)}
            </TableBody>
        </Table>
    </Box>;
}


export default memo(PositionsTable)